import { useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';

/**
 * Per-org subscription lock helper (PR 6). Lock state rides the session
 * payload (`organization.subscription_locked`) — no new API calls. The web app
 * is multi-org-at-once, so locking is INLINE per org: rails always work, only a
 * locked org's content locks. A full-screen takeover happens ONLY when every
 * membership is locked (see `allOrgsLocked`).
 */
export interface OrgLockInfo {
  locked: boolean;
  orgName: string;
  isPersonal: boolean;
  /** Admin of THIS org (roles include 'org_admin'), not the global current role. */
  isAdmin: boolean;
  graceDeadline: string | null;
}

export interface OrgLockState {
  isOrgLocked: (orgId: string | null | undefined) => boolean;
  lockInfoFor: (orgId: string | null | undefined) => OrgLockInfo | null;
  /** True when the user has ≥1 org and every org is locked. */
  allOrgsLocked: boolean;
}

export function useOrgLockState(): OrgLockState {
  const { organizations } = useAuth();

  const isOrgLocked = useCallback(
    (orgId: string | null | undefined): boolean => {
      if (!orgId) return false;
      const membership = organizations.find((o) => o.organization_id === orgId);
      return Boolean(membership?.organization.subscription_locked);
    },
    [organizations]
  );

  const lockInfoFor = useCallback(
    (orgId: string | null | undefined): OrgLockInfo | null => {
      if (!orgId) return null;
      const membership = organizations.find((o) => o.organization_id === orgId);
      if (!membership) return null;
      return {
        locked: Boolean(membership.organization.subscription_locked),
        orgName: membership.organization.name,
        isPersonal: Boolean(membership.organization.is_personal),
        isAdmin: membership.roles.some((r) => r.role === 'org_admin'),
        graceDeadline: membership.organization.grace_deadline ?? null,
      };
    },
    [organizations]
  );

  const allOrgsLocked =
    organizations.length > 0 &&
    organizations.every((o) => o.organization.subscription_locked === true);

  return { isOrgLocked, lockInfoFor, allOrgsLocked };
}

export default useOrgLockState;
